import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Gift, Ticket, ExternalLink, Info } from 'lucide-react';
import { Button } from '../components/ui/button';

const LIVRET_PDF_URL = "https://customer-assets.emergentagent.com/job_cultural-events-5/artifacts/arysj7bk_BRIOUS%E2%80%99O%20FOLIES%20%2815%29.pdf";

export default function Events() {
  const { t } = useTranslation();
  const widgetRef = useRef(null);

  useEffect(() => {
    // Remove existing script to force re-initialization on SPA navigation
    const existingScript = document.querySelector('script[src="https://www.billetweb.fr/js/export.js"]');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.src = 'https://www.billetweb.fr/js/export.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (widgetRef.current) {
        const iframes = widgetRef.current.querySelectorAll('iframe');
        iframes.forEach(iframe => iframe.remove());
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="events-page">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-fuchsia-500/10 via-transparent to-lime-500/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="text-sm font-medium uppercase tracking-widest text-fuchsia-500 mb-4 block">
              {t('eventsExt.calendar2026')}
            </span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6">
              {t('eventsExt.title')}
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {t('eventsExt.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <a href={LIVRET_PDF_URL} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary" data-testid="download-livret-btn">
                  <Download className="mr-2 h-4 w-4" />
                  {t('eventsExt.downloadLivret')}
                </Button>
              </a>
              <Link to="/infos-pratiques">
                <Button variant="outline" className="border-2 border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-6" data-testid="infos-pratiques-btn">
                  <Info className="mr-2 h-4 w-4" />
                  Infos Pratiques
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tombola - Featured */}
      <section className="py-12 bg-gradient-to-r from-amber-50 via-fuchsia-50 to-amber-50" data-testid="tombola-featured">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-bold uppercase tracking-widest mb-4">
              <Gift className="inline h-4 w-4 mr-2" />
              {t('eventsExt.featured')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900">
              {t('eventsExt.grandeTombola')}
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_cultural-events-5/artifacts/opu6egl1_BRIOUS%E2%80%99O%20FOLIES%20%287%29.png"
                alt="Tombola - Voyage en Montgolfière"
                className="w-full h-auto"
              />
            </div>
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-fuchsia-600 mb-4">
                {t('eventsExt.winBalloon')}
              </h3>
              <p className="text-zinc-600 mb-6 text-lg">
                {t('eventsExt.tombolaDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                <div className="bg-white rounded-xl p-4 shadow-md text-center">
                  <p className="text-3xl font-bold text-amber-600">5&euro;</p>
                  <p className="text-sm text-zinc-500">{t('eventsExt.ticket')}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-md text-center">
                  <p className="text-lg font-bold text-zinc-900">8 Nov. 2026</p>
                  <p className="text-sm text-zinc-500">{t('eventsExt.drawAt')}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="https://www.helloasso.com/associations/brious-o-folies/evenements/voyage-en-mongolfiere"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg shadow-lg" data-testid="tombola-participer-btn">
                    <Ticket className="mr-2 h-5 w-5" />
                    {t('eventsExt.participateTombola')}
                  </Button>
                </a>
                <Link to="/tombola">
                  <Button variant="outline" className="border-2 border-fuchsia-500 text-fuchsia-600 rounded-full px-6 py-6">
                    {t('eventsExt.learnMore')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Billetweb Widget */}
      <section className="py-16" data-testid="billetweb-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('eventsExt.reserve')}
            </h2>
          </div>
          <div
            ref={widgetRef}
            className="rounded-2xl overflow-hidden shadow-lg border border-zinc-200 bg-white"
            data-testid="billetweb-widget"
          >
            <a
              title="Vente de billets en ligne"
              href="https://www.billetweb.fr/multi_event.php?multi=44773"
              className="shop_frame"
              target="_blank"
              rel="noopener noreferrer"
              data-src="https://www.billetweb.fr/multi_event.php?multi=44773"
              data-max-width="100%"
              data-initial-height="600"
              data-scrolling="no"
              data-id="44773"
              data-resize="1"
            >
              Vente de billets en ligne
            </a>
          </div>
        </div>
      </section>

      {/* CTA Membership */}
      <section className="py-12 bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-4">
            {t('eventsExt.becomeMember')}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {t('eventsExt.memberDesc')}
          </p>
          <Link to="/adhesion">
            <Button className="bg-white text-fuchsia-600 hover:bg-white/90 rounded-full px-10 py-6 text-lg shadow-lg" data-testid="cta-adhesion-btn">
              {t('eventsExt.memberFor10')}
              <ExternalLink className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
